import React, { useState } from 'react';
import { Grid, Paper, Box, useTheme, useMediaQuery } from '@mui/material';
import { Task, TimelineEvent } from '../../../types';
import { CalendarHeader } from './CalendarHeader';
import { CalendarGrid } from './CalendarGrid';
import { CalendarLegend } from './CalendarLegend';
import { UpcomingEvents } from './UpcomingEvents';
import { useCalendar } from './useCalendar';
import { CalendarEventDialog } from './CalendarEventDialog';
import dayjs from 'dayjs';

interface CalendarTabProps {
  tasks: Task[];
  keyDates: TimelineEvent[];
  onAddEvent?: (event: Omit<TimelineEvent, 'id'>) => void;
  caseName: string;
}

export const CalendarTab: React.FC<CalendarTabProps> = ({
  tasks,
  keyDates,
  onAddEvent,
  caseName,
}) => {
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    currentDate,
    days,
    upcomingEvents,
    handlePrevMonth,
    handleNextMonth,
  } = useCalendar(tasks, keyDates);

  const handleAddEvent = (date: dayjs.Dayjs) => {
    setSelectedDate(date);
    setEventDialogOpen(true);
  };

  const handleEventSave = (eventData: any) => {
    if (onAddEvent) {
      onAddEvent({
        date: eventData.date,
        description: eventData.title,
        type: 'custom',
        addedBy: '1', // Current user ID
        metadata: {
          eventType: eventData.type,
          priority: eventData.priority,
        },
      });
    }
    setEventDialogOpen(false);
    setSelectedDate(null);
  };

  return (
    <Grid container spacing={3}>
      {/* Main Calendar Section */}
      <Grid item xs={12} md={8}>
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <CalendarHeader
            currentDate={currentDate}
            onPrevMonth={handlePrevMonth}
            onNextMonth={handleNextMonth}
            days={days}
            caseName={caseName}
            isMobile={isMobile}
          />
          <CalendarGrid
            currentDate={currentDate}
            days={days.flat()}
            onAddEvent={handleAddEvent}
            isMobile={isMobile}
          />
          <Box sx={{ mt: 3 }}>
            <CalendarLegend isMobile={isMobile} />
          </Box>
        </Paper>
      </Grid>

      {/* Upcoming Events Section */}
      <Grid item xs={12} md={4}>
        <Paper 
          sx={{ 
            p: 3,
            height: '100%',
            background: theme.palette.mode === 'light'
              ? 'linear-gradient(135deg, rgba(30, 58, 95, 0.03), rgba(45, 78, 125, 0.05))'
              : 'linear-gradient(135deg, rgba(19, 40, 68, 0.2), rgba(30, 58, 95, 0.3))',
          }}
        >
          <UpcomingEvents 
            events={upcomingEvents} 
            isMobile={isMobile}
          />
        </Paper>
      </Grid>

      <CalendarEventDialog
        open={eventDialogOpen}
        onClose={() => {
          setEventDialogOpen(false);
          setSelectedDate(null);
        }}
        onSave={handleEventSave}
        selectedDate={selectedDate || undefined}
        isMobile={isMobile}
      />
    </Grid>
  );
};